import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Coins, TrendingUp, Wallet, ArrowUpRight, ArrowDownRight, PiggyBank, Info, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { loginRedirect } from "@/components/Layout";

const METAL_META = {
  gold: { color: "#DDA77B", tint: "#FFF3EC", label: "Gold" },
  silver: { color: "#94A3B8", tint: "#F1F5F9", label: "Silver" },
  platinum: { color: "#64748B", tint: "#F1F5F9", label: "Platinum" },
  copper: { color: "#B45309", tint: "#FEF3C7", label: "Copper" },
};

export default function MetalPocket() {
  const { user } = useAuth();
  const [prices, setPrices] = useState(null);  // null = loading
  const [wallet, setWallet] = useState(null);
  const [txs, setTxs] = useState([]);
  const [metal, setMetal] = useState("gold");
  const [amount, setAmount] = useState(500);
  const [busy, setBusy] = useState(false);
  const [withdrawG, setWithdrawG] = useState("");
  const [withdrawMetal, setWithdrawMetal] = useState("gold");

  const load = async () => {
    // Prices and wallet load independently: a price outage must not hide the user's holdings.
    api.get("/commodities/prices").then(p => setPrices(p.data))
      .catch(() => setPrices({ status: "UNAVAILABLE" }));
    if (user) {
      try {
        const [w, t] = await Promise.all([api.get("/metal-pocket/wallet"), api.get("/metal-pocket/transactions")]);
        setWallet(w.data);
        setTxs(t.data);
      } catch { toast.error("Could not load your Metal Pocket wallet"); }
    }
  };
  useEffect(() => { load(); }, [user]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center">
        <PiggyBank size={40} className="mx-auto text-[#E05E36] mb-3" />
        <h2 className="heading text-3xl font-extrabold mb-2">Login to open your Metal Pocket</h2>
        <p className="text-stone-600 mb-6">Start a ₹50 gullak SIP in real-time gold, silver, platinum or copper.</p>
        <Button onClick={loginRedirect} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="metal-login-btn">Login with Google</Button>
      </div>
    );
  }

  const invest = async () => {
    const n = Number(amount);
    if (!n || n < 50) { toast.error("Minimum SIP is ₹50"); return; }
    setBusy(true);
    try {
      const r = await api.post("/metal-pocket/invest", { metal, amount_inr: n });
      toast.success(`Bought ${r.data.lot.quantity_g.toFixed(4)}g ${metal} @ ₹${r.data.lot.price_per_g_at_buy.toFixed(2)}/g`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Investment failed");
    } finally { setBusy(false); }
  };

  const withdraw = async () => {
    const n = Number(withdrawG);
    if (!n || n <= 0) { toast.error("Enter grams to withdraw"); return; }
    setBusy(true);
    try {
      const r = await api.post("/metal-pocket/withdraw", { metal: withdrawMetal, quantity_g: n });
      if (r.data.interest_earned) {
        toast.success(`Withdrew ₹${r.data.payout_inr} · Bonus interest ₹${r.data.interest_inr} for ${r.data.holding_days_avg} days held`);
      } else {
        toast.success(`Withdrew ₹${r.data.payout_inr} · Held ${r.data.holding_days_avg} days (below 1 yr, no bonus)`);
      }
      setWithdrawG("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Withdraw failed");
    } finally { setBusy(false); }
  };

  const pricesLoaded = prices !== null;
  const px = prices || {};
  const canPrice = px.status === "LIVE" || px.status === "DELAYED";
  const totalInvested = wallet?.totals?.invested_inr || 0;
  const totalValue = wallet?.totals?.current_value_inr || 0;
  const eligibleInterest = wallet?.totals?.eligible_interest_inr || 0;
  const profit = totalValue + eligibleInterest - totalInvested;

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-8">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-[#2D5A27] text-white px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.18em]">
            <PiggyBank size={12} /> Metal Pocket · Gullak SIP
          </div>
          <h1 className="heading text-3xl md:text-5xl font-extrabold text-stone-900 mt-3">Turn ₹50 into <span className="text-[#DDA77B]">real gold</span>.</h1>
          <p className="text-stone-600 mt-2 max-w-xl">Buy fractional gold, silver, platinum &amp; copper at live market price. Hold 1+ year to earn <b className="text-[#2D5A27]">+2% annual interest</b> on face value from <b>Metal Pocket</b>.</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold" data-testid="metal-price-freshness">
            {!pricesLoaded ? "Loading prices…"
              : !canPrice
              ? "Prices unavailable · no validated market quote — buying is paused"
              : `${px.status === "LIVE" ? "LIVE" : "DELAYED"} ₹/g · ${px.source || ""} · source ${px.source_ts ? new Date(px.source_ts).toLocaleString() : "—"} · GST excl. estimate`}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 justify-end">
            {["gold", "silver", "platinum", "copper"].map(m => (
              <div key={m} className="text-xs">
                <span className="capitalize font-bold text-stone-800">{m === "gold" ? "gold 24K est." : m}</span> <span className="text-[#2D5A27] font-extrabold">{px[m] != null ? `₹${Number(px[m]).toLocaleString("en-IN")}` : "—"}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Portfolio summary */}
      <div className="mt-6 grid md:grid-cols-4 gap-3">
        <StatBox label="Invested" value={totalInvested} icon={Wallet} tint="#FFF3EC" color="#E05E36" />
        <StatBox label="Current value" value={totalValue} icon={TrendingUp} tint="#2D5A27/10" color="#2D5A27" />
        <StatBox label="Eligible interest" value={eligibleInterest} icon={Coins} tint="#FFF9E6" color="#B45309" small="only lots held ≥ 1 yr" />
        <StatBox label={profit >= 0 ? "Profit" : "Loss"} value={Math.abs(profit)} icon={profit >= 0 ? ArrowUpRight : ArrowDownRight} tint={profit >= 0 ? "#F0FDF4" : "#FEF2F2"} color={profit >= 0 ? "#2D5A27" : "#B91C1C"} />
      </div>

      <div className="mt-6 grid lg:grid-cols-12 gap-6">
        {/* Buy card */}
        <div className="lg:col-span-4 bg-white border border-stone-200 rounded-3xl p-6 h-fit">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Start / Add SIP</div>
          <h3 className="heading text-2xl font-extrabold text-stone-900 mt-1">Invest in metal</h3>

          <div className="mt-4 space-y-3">
            <div>
              <label className="text-xs font-bold text-stone-500">Metal</label>
              <Select value={metal} onValueChange={setMetal}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="metal-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["gold","silver","platinum","copper"].map(m => <SelectItem key={m} value={m}>{METAL_META[m].label}{canPrice && px[m] != null ? ` · ₹${Number(px[m]).toLocaleString("en-IN")}/g` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold text-stone-500">Amount (₹)</label>
              <Input type="number" min="50" value={amount} onChange={e => setAmount(e.target.value)} className="mt-1 rounded-xl" data-testid="metal-amount" />
              <div className="mt-1 flex gap-1.5 flex-wrap">
                {[50, 100, 500, 1000, 5000].map(v => (
                  <button key={v} type="button" onClick={() => setAmount(v)} className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-stone-100 hover:bg-stone-200" data-testid={`quick-${v}`}>₹{v}</button>
                ))}
              </div>
            </div>
            {canPrice && px[metal] && amount >= 50 && (
              <div className="text-xs bg-[#FFF3EC] p-3 rounded-xl">
                You'll receive <b className="text-stone-900">{(amount / px[metal]).toFixed(4)} g</b> {metal} at ₹{px[metal].toLocaleString("en-IN")}/g
              </div>
            )}
            <Button onClick={invest} disabled={busy || !canPrice} className="w-full rounded-full bg-[#E05E36] hover:bg-[#c94e2a] gap-2" data-testid="invest-btn">
              <PiggyBank size={16} /> Invest ₹{amount}
            </Button>
          </div>

          <div className="mt-5 pt-4 border-t border-stone-100 text-xs text-stone-500 flex items-start gap-2">
            <Info size={13} className="mt-0.5 shrink-0" />
            Withdraw within 12 months: only current market value. Hold ≥ 1 year: +2% annual interest on face value paid by Metal Pocket.
          </div>
        </div>

        {/* Wallet + Withdraw */}
        <div className="lg:col-span-8 space-y-4">
          <div className="bg-white border border-stone-200 rounded-3xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Your holdings</div>
              <div className="text-[11px] text-stone-500 flex items-center gap-1"><Clock size={11} /> Interest-eligible after 365 days per lot</div>
            </div>
            {wallet?.wallet?.length > 0 ? (
              <div className="grid sm:grid-cols-2 gap-3">
                {wallet.wallet.map(w => {
                  const meta = METAL_META[w.metal];
                  return (
                    <motion.div key={w.metal} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl p-4 border" style={{ background: meta.tint, borderColor: meta.color + "30" }} data-testid={`holding-${w.metal}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-9 h-9 rounded-xl grid place-items-center text-white" style={{ background: meta.color }}><Coins size={16} /></div>
                          <div>
                            <div className="heading font-bold text-stone-900">{meta.label}</div>
                            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{w.lots_count} lots · {w.min_days_held}-{w.max_days_held}d</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="heading text-lg font-extrabold text-stone-900">{w.quantity_g.toFixed(4)} g</div>
                          <div className={`text-[11px] font-bold ${w.profit_inr >= 0 ? "text-[#2D5A27]" : "text-red-600"}`}>{w.profit_pct >= 0 ? "+" : ""}{w.profit_pct}%</div>
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-1 text-[10px]">
                        <Stat label="Invested" val={`₹${w.invested_inr.toLocaleString("en-IN")}`} />
                        <Stat label="Now worth" val={`₹${w.current_value_inr.toLocaleString("en-IN")}`} />
                        <Stat label="+ Interest" val={`₹${w.eligible_interest_inr.toLocaleString("en-IN")}`} highlight={w.eligible_interest_inr > 0} />
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-stone-500 text-sm">No holdings yet. Start with ₹50 →</div>
            )}
          </div>

          {/* Withdraw */}
          {wallet?.wallet?.length > 0 && (
            <div className="bg-white border border-stone-200 rounded-3xl p-6">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Withdraw or redeem</div>
                  <h3 className="heading text-xl font-extrabold text-stone-900 mt-1">Cash out — or take real metal in hand</h3>
                </div>
                <Link to="/dealers" className="text-xs font-bold text-[#E05E36] flex items-center gap-1" data-testid="visit-dealers">Redeem physical at a dealer →</Link>
              </div>
              <div className="mt-3 grid sm:grid-cols-3 gap-3">
                <Select value={withdrawMetal} onValueChange={setWithdrawMetal}>
                  <SelectTrigger className="rounded-xl" data-testid="withdraw-metal"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {wallet.wallet.map(w => <SelectItem key={w.metal} value={w.metal}>{METAL_META[w.metal].label} · {w.quantity_g.toFixed(4)}g available</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" step="any" placeholder="Grams to withdraw" value={withdrawG} onChange={e => setWithdrawG(e.target.value)} className="rounded-xl" data-testid="withdraw-grams" />
                <Button onClick={withdraw} disabled={busy || !withdrawG} className="rounded-full bg-[#2D5A27] hover:bg-[#24491f]" data-testid="withdraw-btn">Withdraw cash</Button>
              </div>
              <div className="mt-2 text-[11px] text-stone-500">Cash withdraw uses FIFO with +2% bonus on lots held ≥ 365 days · For physical delivery, use a Metal Pocket dealer partner.</div>
            </div>
          )}

          {/* Transactions */}
          <div className="bg-white border border-stone-200 rounded-3xl p-6">
            <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Transaction history</div>
            <div className="mt-3 divide-y divide-stone-100">
              {txs.length === 0 && <div className="text-sm text-stone-500 py-6 text-center">No transactions yet.</div>}
              {txs.map(t => (
                <div key={t.tx_id} className="py-2 flex items-center gap-3 text-sm" data-testid={`tx-${t.tx_id}`}>
                  <div className={`w-8 h-8 rounded-xl grid place-items-center shrink-0 ${t.action === "invest" ? "bg-[#FFF3EC] text-[#E05E36]" : "bg-[#2D5A27]/10 text-[#2D5A27]"}`}>
                    {t.action === "invest" ? <ArrowDownRight size={16} /> : <ArrowUpRight size={16} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-stone-900 capitalize">{t.action} · {t.metal}</div>
                    <div className="text-[11px] text-stone-500">{new Date(t.created_at).toLocaleString()} · {t.quantity_g.toFixed(4)}g @ ₹{Math.round(t.price_per_g)}/g{t.interest_inr > 0 && <> · <span className="text-[#2D5A27] font-bold">+₹{t.interest_inr} interest</span></>}</div>
                  </div>
                  <div className={`heading font-extrabold ${t.action === "invest" ? "text-stone-800" : "text-[#2D5A27]"}`}>
                    {t.action === "invest" ? "−" : "+"}₹{Number(t.amount_inr + (t.interest_inr || 0)).toLocaleString("en-IN")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, icon: Icon, tint, color, small }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl grid place-items-center" style={{ background: color + "18", color }}><Icon size={16} /></div>
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{label}</div>
      </div>
      <div className="heading text-2xl font-extrabold mt-2" style={{ color }}>₹{Number(value).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
      {small && <div className="text-[10px] text-stone-500 mt-1">{small}</div>}
    </div>
  );
}
function Stat({ label, val, highlight }) {
  return (
    <div>
      <div className="text-stone-500 uppercase tracking-widest text-[9px] font-bold">{label}</div>
      <div className={`font-bold ${highlight ? "text-[#2D5A27]" : "text-stone-800"}`}>{val}</div>
    </div>
  );
}
